import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearOAuthState, getOAuthState, getOAuthProvider, parseProviderFromState } from '../utils/sessionAuth'
import { completeOAuthCallback } from '../api/user'
import { getAppRedirectUri } from '../utils/appOrigin'
import '../style/views/LoginView.css'

type RedirectViewProps = {
  onAuthResolved: (session: {
    email: string | null
    exp?: number
    token: string
    userId: string | null
    firstName?: string
    lastName?: string
    provider?: 'google' | 'azure' | 'apple' | null
  }) => void
}

function RedirectView({ onAuthResolved }: RedirectViewProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const provider = getOAuthProvider()
  const [message, setMessage] = useState(
    provider === 'azure' ? 'Signing you in with Microsoft…' : 'Signing you in with Google…'
  )
  const [error, setError] = useState<string | null>(null)
  const hasCalledRef = useRef(false)

  useEffect(() => {
    const completeLogin = async () => {
      if (hasCalledRef.current) return
      hasCalledRef.current = true

      const params = new URLSearchParams(location.search)
      const fragmentParams = new URLSearchParams(location.hash.replace(/^#/, ''))
      const code = params.get('code')
      const idToken = params.get('id_token') ?? fragmentParams.get('id_token') ?? fragmentParams.get('credential')
      const state = params.get('state') ?? fragmentParams.get('state')
      const storedState = getOAuthState()

      // Recover provider from state param if localStorage was cleared (e.g. Safari ITP)
      const resolvedProvider = provider ?? parseProviderFromState(state)

      try {
        if (!resolvedProvider) {
          throw new Error('Sign-in provider is missing. Please try again from the login screen.')
        }
        if (!code && !idToken) {
          throw new Error(`Missing authorization response from ${resolvedProvider === 'azure' ? 'Microsoft' : 'Google'}.`)
        }
        if (storedState && !state) throw new Error('Sign-in state was missing. Please try again.')
        if (storedState && state && storedState !== state) {
          throw new Error('Sign-in state did not match. Please try again.')
        }

        const redirectUri = getAppRedirectUri()
        const callbackBody = idToken
          ? { id_token: idToken, state: state ?? undefined }
          : {
              code,
              state: state ?? undefined,
              redirect_uri: redirectUri,
            }

        const tokenJson = await completeOAuthCallback(resolvedProvider as 'google' | 'azure', callbackBody)

        const email = tokenJson?.email as string | undefined
        const token = tokenJson?.token as string | undefined
        const userId = tokenJson?.userId ? String(tokenJson.userId) : null
        const firstName = tokenJson?.first_name as string | undefined
        const lastName = tokenJson?.last_name as string | undefined

        if (!token) {
          throw new Error('Server did not return a session token.')
        }

        onAuthResolved({ email: email ?? null, token, userId, firstName, lastName, provider: resolvedProvider })
        navigate('/', { replace: true })
      } catch (err) {
        console.error(`Failed to handle ${resolvedProvider === 'azure' ? 'Microsoft' : 'Google'} redirect`, err)
        let errorMessage = err instanceof Error ? err.message : `Unable to sign in with ${resolvedProvider === 'azure' ? 'Microsoft' : 'Google'}.`
        if (errorMessage.includes('Azure authentication failed: 400')) {
          errorMessage = 'Incorrect email or password'
        }
        setError(errorMessage)
        setMessage('We could not sign you in.')
      } finally {
        clearOAuthState()
      }
    }

    completeLogin()
  }, [location.hash, location.search, navigate, onAuthResolved, provider])

  if (error) {
    return (
      <div className="logged-out-shell">
        <div className="card" style={{ maxWidth: '420px', textAlign: 'center' }}>
          <h1>Login failed</h1>
          <p className="muted">{error}</p>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate('/', { replace: true })}
            style={{ marginTop: '16px', width: '100%' }}
          >
            Back to login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="logged-out-shell">
      <div className="loading-container">
        <h1>Connecting…</h1>
        <p className="muted">{message}</p>
      </div>
    </div>
  )
}

export default RedirectView
